import { Modal, Descriptions, Tag, Typography, Spin, Divider } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { showErrorNotification } from "../../../utils/notification";
import { CLAIM_STATUS_TAG_COLORS, formatEnumLabel, formatItemKeysList } from "../helper";

const { Text } = Typography;

/** Two columns from the `sm` breakpoint up, one column on a phone. */
const DETAIL_COLUMNS = Object.freeze({ xs: 1, sm: 2 });

/**
 * Read-only detail view for a single reward-kit claim, showing its status,
 * course info, claimed item list, and shipping address. Fetches the claim when it
 * mounts and whenever `claimId` changes.
 *
 * @param {object} props
 * @param {boolean} [props.isModalOpen=false]
 * @param {function} [props.setIsModalOpen] - Receives `false` to close the modal.
 * @param {string} props.claimId - The claim's `uniquePublicId`.
 * @param {string} props.ROOT_URL - Backend origin for the selected environment.
 * @param {string} props.BASE_URL - Path of the claims admin API under `ROOT_URL`.
 */
const ClaimDetailModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  claimId,
  ROOT_URL,
  BASE_URL
}) => {
  const [claimDetails, setClaimDetails] = useState(null);
  const [isLoadingClaimDetails, setIsLoadingClaimDetails] = useState(false);

  const {
    status,
    claimAttempts,
    courseName,
    courseUrlIdentifier,
    createdAt,
    updatedAt,
    adminActionReason,
    items = [],
    shippingDetails = {}
  } = claimDetails || {};

  const {
    fullName,
    phone,
    email,
    addressLine1,
    addressLine2,
    city,
    state,
    pincode,
    country
  } = shippingDetails;

  /** Loads the claim named by `claimId`; does nothing when no claim is selected. */
  const fetchClaimDetails = () => {
    if (!claimId) {
      return;
    }
    setIsLoadingClaimDetails(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/detail`, { params: { rewardClaimId: claimId } })
      .then((response) => {
        setClaimDetails(response.data);
      })
      .catch((error) => {
        showErrorNotification(error);
      })
      .finally(() => {
        setIsLoadingClaimDetails(false);
      });
  };

  /** Closes the modal. */
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    fetchClaimDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  return (
    <Modal
      title="Reward Kit Claim Detail"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width="min(700px, calc(100vw - 32px))"
    >
      {isLoadingClaimDetails && (
        <div style={{ textAlign: "center", padding: "24px" }}>
          <Spin />
        </div>
      )}
      {!isLoadingClaimDetails && claimDetails && (
        <>
          <Descriptions column={DETAIL_COLUMNS} bordered size="small">
            <Descriptions.Item label="Status">
              <Tag color={CLAIM_STATUS_TAG_COLORS[status]}>{formatEnumLabel(status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Claim Attempts">{claimAttempts}</Descriptions.Item>
            <Descriptions.Item label="Course" span={2}>
              <Text strong>{courseName || "TLE Prime"}</Text>
              {courseUrlIdentifier && (
                <Text type="secondary" style={{ marginLeft: "8px", fontSize: "12px" }}>
                  ({courseUrlIdentifier})
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {createdAt ? dayjs(createdAt).format("DD MMM YYYY | HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {updatedAt ? dayjs(updatedAt).format("DD MMM YYYY | HH:mm") : "-"}
            </Descriptions.Item>
            {Boolean(adminActionReason) && (
              <Descriptions.Item label="Admin Action Reason" span={2}>
                {adminActionReason}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Divider orientation="left">Items</Divider>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Item Keys">{formatItemKeysList(items)}</Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Shipping Address</Divider>
          <Descriptions column={DETAIL_COLUMNS} bordered size="small">
            <Descriptions.Item label="Full Name">{fullName}</Descriptions.Item>
            <Descriptions.Item label="Phone">{phone}</Descriptions.Item>
            <Descriptions.Item label="Email" span={2}>
              {email}
            </Descriptions.Item>
            <Descriptions.Item label="Address Line 1" span={2}>
              {addressLine1}
            </Descriptions.Item>
            <Descriptions.Item label="Address Line 2" span={2}>
              {addressLine2 || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="City">{city}</Descriptions.Item>
            <Descriptions.Item label="State">{state}</Descriptions.Item>
            <Descriptions.Item label="Pincode">{pincode}</Descriptions.Item>
            <Descriptions.Item label="Country">{country}</Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  );
};

export default ClaimDetailModal;
